import React, { createContext, useState, useMemo, useContext, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getTheme } from './theme';
import { ThemeName } from './palette';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: (newMode: ThemeMode) => void;
  effectiveMode: 'light' | 'dark';
  colorTheme: ThemeName;
  setColorTheme: (theme: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useAppTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within AppThemeProvider');
  }
  return context;
};

interface AppThemeProviderProps {
  children: React.ReactNode;
}

export const AppThemeProvider: React.FC<AppThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem('themeMode');
    return (stored as ThemeMode) || 'system';
  });

  const [colorTheme, setColorThemeState] = useState<ThemeName>(() => {
    // One-time migration: upgrade existing users from old burgundy "default" to new glass design
    const migrated = localStorage.getItem('themeMigrated_v2');
    const stored = localStorage.getItem('colorTheme');
    if (!migrated) {
      // Force glass on first load after v2 ships, regardless of prior choice.
      // User can still pick another theme afterwards via ThemeSwitcher.
      localStorage.setItem('colorTheme', 'glass');
      localStorage.setItem('themeMigrated_v2', '1');
      return 'glass';
    }
    return (stored as ThemeName) || 'glass';
  });

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const effectiveMode = useMemo<'light' | 'dark'>(() => {
    if (mode === 'system') {
      return prefersDarkMode ? 'dark' : 'light';
    }
    return mode;
  }, [mode, prefersDarkMode]);

  const theme = useMemo(() => getTheme(effectiveMode, colorTheme), [effectiveMode, colorTheme]);

  const toggleTheme = (newMode: ThemeMode) => {
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  const setColorTheme = (newTheme: ThemeName) => {
    setColorThemeState(newTheme);
    localStorage.setItem('colorTheme', newTheme);
  };

  useEffect(() => {
    const root = document.documentElement;
    document.body.style.backgroundColor = theme.palette.background.default;
    root.setAttribute('data-theme', effectiveMode);

    // Sync MUI palette → CSS variables (single source of truth).
    // Glass-bg / glass-border / shadow-* keep their mode-based defaults from index.css.
    root.style.setProperty('--accent', theme.palette.primary.main);
    root.style.setProperty('--accent-soft', alpha(theme.palette.primary.main, 0.12));
    root.style.setProperty('--accent-glow', alpha(theme.palette.primary.main, 0.35));
    root.style.setProperty('--surface-0', theme.palette.background.default);
    root.style.setProperty('--text-primary', theme.palette.text.primary);
    root.style.setProperty('--text-secondary', theme.palette.text.secondary);
    root.style.setProperty('--text-tertiary', theme.palette.text.disabled);
  }, [theme, effectiveMode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, effectiveMode, colorTheme, setColorTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
